import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useBlocker } from "react-router-dom";
import { get, post, put } from "../../../../api";
import DatePicker from "../../../common/DatePicker";
import CircularProgress from "@mui/material/CircularProgress";
import moment from "moment";
import {
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  DialogActions,
  Dialog,
  Typography,
  Grid,
  Autocomplete,
  Box,
} from "@mui/material";
import { getSiteAssets, getSiteLayout } from "../../../../store/thunk/site";
import { blueGrey } from "@mui/material/colors";
import { ROLE } from "../../../../Constant/Role";
import SortableOrderControl from "../../../common/SortableOrderControl";
import {
  selectLatestSavedWaterOutlets,
  downloadWaterTemperaturePdf,
} from "./shared/waterTemperaturePdf";

const ADMIN_EDITABLE_FIELDS = [
  "outletType",
  "temperature",
  "normalRunTime",
  "usageFrequency",
  "floor",
  "room",
];

const TRACKED_UNSAVED_FIELDS = ["sortOrder", ...ADMIN_EDITABLE_FIELDS];

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const buildSavedOutletBaseline = (rows = []) =>
    rows.reduce((acc, row) => {
      if (!row?.id || !row?.assetId) return acc;
      acc[String(row.id)] = TRACKED_UNSAVED_FIELDS.reduce((snapshot, field) => {
        snapshot[field] = normalizeComparableValue(row?.[field]);
        return snapshot;
      }, {});
      return acc;
    }, {});

const SurveyWaterTemperatureMonitoring = ({
                                            checkId,
                                            siteAssets,
                                            siteLayout,
                                            getSiteAssets,
                                            siteSelectedForGlobal,
                                            getSiteLayout,
                                            loggedInUserData,
                                            repeatFrequency,
                                            leadUserID
                                          }) => {
  const [outletoptions, setoutletoptions] = useState([]);
  const [tempratureoptions, settempratureoptions] = useState([]);
  const [normruntime, setnormruntime] = useState([]);
  const [readingPop, setReadingPop] = useState(null);
  const [readingPopShow, setReadingPopShow] = useState(false);
  const [showHistory, setShowHistory] = useState(null);
  const [formData, setFormData] = useState([{}]);
  const [formData2, setFormData2] = useState({});
  const [completed, setCompleted] = useState(false);
  const [alldata, setalldata] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [sortOrderDirty, setSortOrderDirty] = useState(false);
  const [draggedRowIndex, setDraggedRowIndex] = useState(null);
  const [savedOutletBaseline, setSavedOutletBaseline] = useState({});

  const isAdmin = loggedInUserData?.role === ROLE.ADMIN;

  const getChangedFields = (row) => {
    if (!isAdmin || !row?.id) return [];
    const baseline = savedOutletBaseline[String(row.id)];
    if (!baseline) return [];

    return TRACKED_UNSAVED_FIELDS.filter(
        (field) =>
            normalizeComparableValue(row?.[field]) !==
            normalizeComparableValue(baseline?.[field])
    );
  };

  const getConfigChangedFields = (row) =>
      getChangedFields(row).filter((field) => field !== "sortOrder");

  const hasUnsavedAdminChanges =
      isAdmin && formData.some((row) => getChangedFields(row).length > 0);

  const blocker = useBlocker(hasUnsavedAdminChanges);

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    const leaveWithoutSaving = window.confirm(
        "You have unsaved Water Temperature Monitoring changes. Leave without saving them?"
    );

    if (leaveWithoutSaving) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedAdminChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedAdminChanges]);

  useEffect(() => {
    if (siteSelectedForGlobal?.siteId) {
      getSiteAssets(siteSelectedForGlobal?.siteId);
      getSiteLayout(siteSelectedForGlobal?.siteId);
    }
    getSurvey();
  }, []);

  const getSurvey = async () => {
    setIsLoading(true);
    const outlettypes = await get("/api/lov/SITE_CHECK_SURVEY_OUTLET_TYPE");
    setoutletoptions(outlettypes?.map((l) => l.lovValue));
    const tempratureoptionstypes = await get(
        "/api/lov/SITE_CHECK_SURVEY_TEMPRATURE"
    );
    settempratureoptions(tempratureoptionstypes?.map((l) => l.lovValue));
    const normruntimetypes = await get(
        "/api/lov/SITE_CHECK_SURVEY_NORM_RUN_TIME"
    );
    setnormruntime(normruntimetypes?.map((l) => l.lovValue));
    let data = await get("/api/site-check/water-outlet-temp/" + checkId);
    data = data.filter(d=> d.assetId);
    if (data.length > 0) {
      data.forEach((_) => {
        _.completed = true;
      });
      //data = data.filter(d=> !d.assetId === null);

      setalldata(data);
      //data = data.reverse();
      data = selectLatestSavedWaterOutlets(data);
      setFormData(data);
      setSavedOutletBaseline(buildSavedOutletBaseline(data));
    } else {
      setSavedOutletBaseline({});
    }
    setSortOrderDirty(false);
    setDraggedRowIndex(null);
    setIsLoading(false);
  };

  const downloadLatestPdf = async () => {
    if (isDownloadingPdf || !checkId) return;
    setIsDownloadingPdf(true);
    try {
      // Never use the editable form state: it can contain unsaved changes.
      // Re-fetch all persisted readings and bypass cached GET responses.
      const savedRows = await get(
        `/api/site-check/water-outlet-temp/${checkId}?pdfRefresh=${Date.now()}`
      );
      if (!Array.isArray(savedRows)) {
        throw new Error("Invalid Water Temperature response.");
      }
      if (!savedRows.some((row) => row?.assetId)) {
        toast.info("No saved water temperature outlets to download.");
        return;
      }
      if (hasUnsavedAdminChanges) {
        toast.info("PDF includes saved data only. Unsaved changes are not included.");
      }

      downloadWaterTemperaturePdf({
        records: savedRows,
        siteName: siteSelectedForGlobal?.name || siteSelectedForGlobal?.siteName || "Site",
        siteId: siteSelectedForGlobal?.siteId,
        checkId,
        siteAssets,
        siteLayout,
      });
    } catch (error) {
      console.error("Could not generate Water Temperature PDF", error);
      toast.error("Could not download the Water Temperature PDF. Please try again.");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const isDuplicate = (newItem) => {
    const completedItems = formData.filter((item) => item.completed);
    return (
        completedItems.filter(
            (i) =>
                newItem?.assetId === i?.assetId &&
                newItem?.floor === i?.floor &&
                newItem?.normalRunTime === i?.normalRunTime &&
                newItem?.outletType === i?.outletType &&
                newItem?.temperature === i?.temperature &&
                newItem?.room === i?.room
        )?.length > 0
    );
  };

  useEffect(() => {
    formData.forEach((item, idx) => {
      if (!item.completed) {
        if (isDuplicate(item)) {
          toast.error("Duplicate item found:");
        }
      }
    });
  }, [formData]);

  const getName = (idx) => {
    let res = "";
    const floor = siteLayout.filter(
        (s) => String(s.id) === String(formData[idx]?.floor)
    );
    const room = siteLayout.filter(
        (s) => String(s.id) === String(formData[idx]?.room)
    );
    if (floor.length > 0) {
      res = res + floor[0].nodeName;
    } else {
      res = res + formData[idx]?.floor;
    }
    if (room.length > 0) {
      res = res + " > " + room[0].nodeName;
    } else {
      res = res + " > " + formData[idx]?.room;
    }
    return res;
  };

  const getNodeName = (id) => {
    let res = "";
    const node = siteLayout.filter(
        (s) => String(s.id) === String(id)
    );

    return node?.[0]?.nodeName??id ;
  };

  const getLayoutNodeByStoredValue = (value, nodeType) =>
      siteLayout.find(
          (site) =>
              site.nodeType === nodeType &&
              (String(site.id) === String(value) || site.nodeName === value)
      );

  const getLayoutSelectValue = (value, nodeType) => {
    if (value === null || value === undefined || value === "null") return "";
    return getLayoutNodeByStoredValue(value, nodeType)?.nodeName ?? value;
  };

  const handleInputChange = (e, idx) => {
    const { name, value } = e.target;
    const uformData = [...formData];
    const currentRow = formData[idx];
    const isAdminPersistedEdit =
        isAdmin && Boolean(currentRow?.id && currentRow?.assetId);

    const udata = {
      ...currentRow,
      [name]: value,
      ...(isAdminPersistedEdit ? {} : { update: true }),
    };

    if (name === "floor" && value !== currentRow?.floor) {
      udata.room = "";
    }

    uformData[idx] = udata;
    setFormData(uformData);
  };

  const handleInputChange2 = (e) => {
    const { name, value } = e.target;
    const udata = {
      ...formData2,
      [name]: value
    };
    setFormData2(udata);
  };

  const addSiteCheckSurvey = async (event) => {
    event.preventDefault();
    const form = event.target;
    if (!form.checkValidity()) {
      form.reportValidity();
    }
    let i =0;
    for (const data of formData) {
      i=i+1;
      if (!data.completed || data.update) {
        if (isDuplicate(data) && !data.update) {
          toast.error("Duplicate data!!!");
          return;
        }
        data.checkId = checkId;
        data.status = "Open";
        if (data.update) {
          data.id = undefined;
        }
        if (data.r1Date) {
          //data.r1Date = new Date(data.r1Date.toISOString().slice(0, 10));
          data.r2Date = data.r1Date;
          data.r3Date = data.r1Date;
        }
        if(data.assetId && data.reading1 && data.reading2 && data.reading3) {
          await post("/api/site-check/water-outlet-temp", data);

          break;
        }


      }
    }
    toast.success("Water outlet temperature data saved.");
    //getSurvey();
  };

  const movePersistedOutletToPosition = (sourceRowId, targetPosition) => {
    if (!isAdmin || !sourceRowId) return;

    const persistedRows = formData.filter((item) => item?.id && item?.assetId);
    const sourceIndex = persistedRows.findIndex(
        (item) => String(item.id) === String(sourceRowId)
    );
    const requestedPosition = Number(targetPosition);

    if (
        sourceIndex < 0 ||
        !Number.isInteger(requestedPosition) ||
        requestedPosition < 1 ||
        requestedPosition > persistedRows.length
    ) {
      return;
    }

    const reorderedPersistedRows = [...persistedRows];
    const [movedRow] = reorderedPersistedRows.splice(sourceIndex, 1);
    reorderedPersistedRows.splice(requestedPosition - 1, 0, movedRow);

    const normalizedPersistedRows = reorderedPersistedRows.map((item, index) => ({
      ...item,
      sortOrder: index + 1,
    }));
    const newRows = formData.filter((item) => !item?.id || !item?.assetId);
    const normalized = [...normalizedPersistedRows, ...newRows];

    setFormData(normalized);
    setSortOrderDirty(
        normalizedPersistedRows.some((row) =>
            getChangedFields(row).includes("sortOrder")
        )
    );
    setDraggedRowIndex(null);
  };

  const handleCompletedOutletDrop = (fromIndex, toIndex) => {
    if (!isAdmin || fromIndex === null || fromIndex === toIndex) {
      setDraggedRowIndex(null);
      return;
    }

    const sourceRow = formData?.[fromIndex];
    const targetRow = formData?.[toIndex];
    if (!sourceRow?.id || !sourceRow?.assetId || !targetRow?.id || !targetRow?.assetId) {
      setDraggedRowIndex(null);
      return;
    }

    const persistedRows = formData.filter((item) => item?.id && item?.assetId);
    const targetPosition =
        persistedRows.findIndex((item) => String(item.id) === String(targetRow.id)) + 1;

    movePersistedOutletToPosition(sourceRow.id, targetPosition);
  };

  const addSiteCheckSurvey2 = async (event) => {
    event.preventDefault();
    const form = event.target;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    for (const data of formData) {
      if (!data.completed && (data.update || data.new )) {
        if (isDuplicate(data) && data.new) {
          toast.error("Duplicate data!!!");
          return;
        }
        data.checkId = checkId;
        data.status = "Open";
        if (data.update) {
          data.id = undefined;
        }
        if (data.r1Date) {
          //data.r1Date = new Date(data.r1Date.toISOString().slice(0, 10));
          data.r2Date = data.r1Date;
          data.r3Date = data.r1Date;
        }
        if(data.assetId) {
          await post("/api/site-check/water-outlet-temp", data);
        }

      }
    }

    if (isAdmin && sortOrderDirty) {
      const persistedOutlets = formData.filter(
          (item) => item?.id && item?.assetId
      );

      if (persistedOutlets.length > 0) {
        await put("/api/site-check/water-outlet-temp/sort-order-batch", {
          checkId: Number(checkId),
          orders: persistedOutlets.map((item) => ({
            assetId: Number(item.assetId),
            sortOrder: Number(item.sortOrder),
          })),
        });
      }
    }

    if (isAdmin) {
      const changedOutletConfigs = formData
          .filter((item) => item?.id && item?.assetId)
          .filter((item) => getConfigChangedFields(item).length > 0);

      if (changedOutletConfigs.length > 0) {
        await put("/api/site-check/water-outlet-temp/admin-config-batch", {
          checkId: Number(checkId),
          updates: changedOutletConfigs.map((item) => ({
            id: Number(item.id),
            assetId: Number(item.assetId),
            outletType: item.outletType,
            temperature: item.temperature,
            normalRunTime: item.normalRunTime,
            usageFrequency: item.usageFrequency,
            floor: item.floor,
            room: item.room,
          })),
        });
      }
    }

    toast.success("Water outlet temperature data saved.");
    getSurvey();
  };

  const addReadingSave = (event) => {
    event.preventDefault();

    const form = event.target;
    if (!form.checkValidity()) {
      form.reportValidity();
    }
    const isReading1ok =
        (formData?.[readingPop]?.temperature === "Hot" &&
            Number(formData[readingPop]?.reading1) < 50) ||
        (formData?.[readingPop]?.temperature === "Cold" &&
            Number(formData[readingPop]?.reading1) > 20);
    const isReading2ok =
        (formData?.[readingPop]?.temperature === "Hot" &&
            Number(formData[readingPop]?.reading2) < 50) ||
        (formData?.[readingPop]?.temperature === "Cold" &&
            Number(formData[readingPop]?.reading2) > 20);
    const isReading3ok =
        (formData?.[readingPop]?.temperature === "Hot" &&
            Number(formData[readingPop]?.reading3) < 50) ||
        (formData?.[readingPop]?.temperature === "Cold" &&
            Number(formData[readingPop]?.reading3) > 20);
    if (!isReading1ok && !isReading2ok && !isReading3ok) {
      addSiteCheckSurvey(event);

    } else {
      addSiteCheckSurvey(event);
      const body = {
        type: "Survey",
        status: "Reported",
        observation: formData2.observation,
        desc: `Surevy Water - Outlet Temprature - ${moment(new Date()).format("DD/MM/YYYY")}`,
        requiredAction: formData2.requiredAction,
        riskScore: Number(formData2.likelihood) * Number(formData2.consequence),
        dueDate: new Date(),
        siteId: siteSelectedForGlobal?.siteId,
        userId: loggedInUserData?.id,
        assignedTo: leadUserID,
      }
      put("/api/site/actions", body);
    }
    setReadingPop((prev) => {
      const nextIndex = prev < formData.length - 1 ? prev + 1 : null;
      if(nextIndex === null) {
        setReadingPop(null);
        setReadingPopShow(false);
        getSurvey();
      }
      // Ensure the next index has default/blank values
      setFormData((prevData) => {
        const updatedData = [...prevData];
        updatedData[prev].update = false;
        updatedData[nextIndex] = { ...updatedData[nextIndex], reading1: "", reading2: "", reading3: "", r1Date: new Date().toISOString(), update: true };
        return updatedData;
      });
      return nextIndex;
    });
  };

  const dateFormat = (date) => {
    return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
  };

  const dateFormatFromFrequency = (date) => {
    let daysToAdd = 0;
    if(repeatFrequency === "Daily") {
      daysToAdd = 1
    } else  if(repeatFrequency === "Weekly") {
      daysToAdd = 7
    } else if(repeatFrequency === "Monthly") {
      daysToAdd = 30
    } else if(repeatFrequency === "Yearly") {
      daysToAdd = 365
    }
    return moment(date, "YYYY-MM-DD").add('days', daysToAdd).format("DD/MM/YYYY");
  };

  const isRed1 = formData[readingPop]?.reading1?.length === 0 ? false : (formData?.[readingPop]?.temperature === "Hot" && Number(formData[readingPop]?.reading1) <50) ||
      (formData?.[readingPop]?.temperature === "Cold" && Number(formData[readingPop]?.reading1) > 20);

  const isRed2 = formData[readingPop]?.reading2?.length === 0 ? false : (formData?.[readingPop]?.temperature === "Hot" && Number(formData[readingPop]?.reading2) <50) ||
      (formData?.[readingPop]?.temperature === "Cold" && Number(formData[readingPop]?.reading2) > 20);

  const isRed3 = formData[readingPop]?.reading3?.length === 0 ? false : (formData?.[readingPop]?.temperature === "Hot" && Number(formData[readingPop]?.reading3) <50) ||
      (formData?.[readingPop]?.temperature === "Cold" && Number(formData[readingPop]?.reading3) > 20);

  const isRed =  (isRed1 || isRed2 || isRed3) && formData[readingPop]?.reading1?.length > 0 && formData[readingPop]?.reading2?.length > 0
      && formData[readingPop]?.reading3?.length > 0


  return (
      <>
        <Dialog
            open={readingPopShow}
            onClose={() => {
              setReadingPop(null);
              setReadingPopShow(false)
              getSurvey()
            }}
            maxWidth="lg"
            fullWidth
        >
          <form onSubmit={addReadingSave}>
            <DialogTitle>
              Add Reading{" "}
              {formData[readingPop]?.assetId
                  ? "(" +
                  siteAssets
                      .filter((a) => a.assetId == formData[readingPop]?.assetId)
                      .map(
                          (option) => option.assetId + " "+ option.assetName + " - " + option.category
                      )[0] +
                  ")"
                  : ""}
            </DialogTitle>
            <DialogContent dividers>
              <Fragment>
                <Grid container>
                  <>
                    <Grid sm={4}>
                      <label htmlFor="outletType">Outlet Type</label>
                      <input
                          style={{ maxWidth: "300px" }}
                          type="text"
                          autoComplete="off"
                          readOnly
                          onFocus={(e) => e.target.removeAttribute("readonly")}
                          className="form-control"
                          id="outletType"
                          disabled
                          value={formData?.[readingPop]?.outletType}
                      />
                    </Grid>
                    <Grid sm={4}>
                      <label htmlFor="outletType">Temperature</label>
                      <input
                          style={{ maxWidth: "300px" }}
                          type="text"
                          autoComplete="off"
                          readOnly
                          onFocus={(e) => e.target.removeAttribute("readonly")}
                          className="form-control"
                          id="outletType"
                          disabled
                          value={formData?.[readingPop]?.temperature}
                      />
                    </Grid>
                    <Grid sm={4}>
                      <label htmlFor="outletType">Location</label>
                      <input
                          style={{ maxWidth: "300px" }}
                          type="text"
                          autoComplete="off"
                          readOnly
                          onFocus={(e) => e.target.removeAttribute("readonly")}
                          className="form-control"
                          id="outletType"
                          disabled
                          value={getName(readingPop)}
                      />
                    </Grid>
                    <Grid sm={12}>
                      <div
                          className="table-responsive"
                          style={{ marginTop: "30px" }}
                      >
                        <table className="table table-bordered f-11">
                          <thead className="table-dark">
                          <tr>
                            <th></th>
                            <th>Test Date</th>
                            <th>Reading</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr>
                            <td>
                              Reading 1{" "}
                              <b style={{ color: "red" }}> 30 seconds </b>
                            </td>
                            <td
                                rowSpan={3}
                                style={{ verticalAlign: "middle" }}
                            >
                              <DatePicker
                                  value={
                                    formData?.[readingPop]?.update
                                        ? formData?.[readingPop]?.r1Date
                                        : moment()
                                  }
                                  onChange={(date) => {
                                    const uformData = [...formData];
                                    const udata = {
                                      ...formData[readingPop],
                                      r1Date: new Date(
                                          date.getTime() -
                                          date.getTimezoneOffset() * 60000
                                      ).toISOString(),
                                      update: true,
                                    };
                                    uformData[readingPop] = udata;
                                    setFormData(uformData);
                                  }}
                              />
                              {/* // <input
                              //   type="date"
                              //   className="form-control"
                              //     name="r1Date"
                              //     required
                              //   onChange={(e) => handleInputChange(e, readingPop)}
                              // /> */}
                            </td>
                            <td>
                              <input
                                  type="number"
                                  onWheel={(e) => e.target.blur()}
                                  className="form-control"
                                  name="reading1"
                                  value={formData?.[readingPop]?.reading1 || ""}
                                  required
                                  style={{
                                    color:
                                        isRed1 ? "red" : "green",
                                    fontWeight: "600",
                                  }}
                                  onChange={(e) =>
                                      handleInputChange(e, readingPop)
                                  }
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>
                              Reading 2{" "}

                              <b style={{ color: "red" }}> 60 seconds </b>
                            </td>
                            <td>
                              <input
                                  type="number"
                                  onWheel={(e) => e.target.blur()}
                                  className="form-control"
                                  name="reading2"
                                  required
                                  value={formData?.[readingPop]?.reading2 || ""}
                                  style={{
                                    color:isRed2 ? "red" : "green",
                                    fontWeight: "600",
                                  }}
                                  onChange={(e) =>
                                      handleInputChange(e, readingPop)
                                  }
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>
                              Reading 3{" "}
                              <b style={{ color: "red" }}> 120 seconds </b>
                            </td>
                            <td>
                              <input
                                  type="number"
                                  onWheel={(e) => e.target.blur()}
                                  required
                                  className="form-control"
                                  name="reading3"
                                  value={formData?.[readingPop]?.reading3 || ""}
                                  style={{
                                    color:isRed3 ? "red" : "green",
                                    fontWeight: "600",
                                  }}
                                  onChange={(e) =>
                                      handleInputChange(e, readingPop)
                                  }
                              />
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </Grid>
                  </>

                  {/* {action && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Action
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger text-light"
                          onClick={() => {
                            setAction2(true);
                            setAction(false);
                          }}
                        >
                          <i className="fas fa-circle-exclamation" /> Add Action
                        </button>
                      </Grid>
                    </Grid>
                  </Grid>
                )} */}
                  {isRed && (
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Action
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Grid item xs={12} sm={12}>
                              <label htmlFor="consequence" name="consequence">
                                Consequence
                              </label>
                              <select
                                  required
                                  className="form-control form-select"
                                  value={formData?.[readingPop]?.consequence}
                                  name="consequence"
                                  onChange={(e) => handleInputChange2(e)}
                              >
                                <option value="">Select </option>
                                {[1, 2, 3, 4, 5].map((num) => (
                                    <option value={num}>{num} </option>
                                ))}
                              </select>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <label htmlFor="likelihood" name="likelihood">
                                Likelihood
                              </label>
                              <select
                                  required
                                  className="form-control form-select"
                                  name="likelihood"
                                  value={formData?.[readingPop]?.likelihood}
                                  onChange={(e) => handleInputChange2(e)}
                              >
                                <option value="">Select </option>
                                {[1, 2, 3, 4, 5].map((num) => (
                                    <option value={num}>{num} </option>
                                ))}
                              </select>
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                p={2}
                                mb={2}
                                style={{
                                  height: "290px",
                                  marginTop: "-20px",
                                }}
                            >
                              <img
                                  src="/RiskScore.png"
                                  alt="Risk Score Matrix"
                                  style={{ width: "100%", height: "100%" }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <label htmlFor="observation" name="observation">
                              Observation
                            </label>
                            <textarea
                                //disabled={quest[idx]?.completed}
                                value={formData?.[readingPop]?.observation}
                                name="observation"
                                className="form-control"
                                id="observation"
                                rows="2"
                                required
                                onChange={(e) => handleInputChange2(e)}
                                style={{
                                  width: "100%",
                                  padding: "10px",
                                  margin: "8px 0",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <label htmlFor="requiredAction" name="requiredAction">
                              Required Action
                            </label>
                            <textarea
                                name="requiredAction"
                                className="form-control"
                                id="requiredAction"
                                rows="2"
                                value={formData?.[readingPop]?.requiredAction}
                                required
                                onChange={(e) => handleInputChange2(e)}
                                style={{
                                  width: "100%",
                                  padding: "10px",
                                  margin: "8px 0",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                  )}
                </Grid>
                {/* <div className="float-end"><small className="alert alert-info p-2">Please click on Save before proceeding to the next Asset</small></div> */}
              </Fragment>
            </DialogContent>
            <DialogActions>
              <Button
                  type="button"
                  onClick={() => { setReadingPop(null);
                    setReadingPopShow(false);
                    getSurvey();
                  }}
                  className="bg-light text-primary"
              >
                Cancel
              </Button>
              <button
                  type="submit"
                  //onClick={addReadingSave}
                  style={{
                    width: "150px",
                    marginBottom: "20px",
                    margin: "10px",
                    float: "right",
                  }}
                  className="btn btn-primary text-white pr-2"
              >
                {readingPop === (formData?.length -1)? 'Save & Close' : 'Next'}
              </button>
              {/* <button
              type="button"
              onClick={(e) => {
                if(!action) {
                  addReadingSave(e);
                  return;
                }
                setReadingPop((prev) => {
                  const nextIndex = prev < formData.length - 1 ? prev + 1 : prev;
                  // Ensure the next index has default/blank values
                  setFormData((prevData) => {
                    const updatedData = [...prevData];
                    updatedData[nextIndex] = { ...updatedData[nextIndex], reading1: "", reading2: "", reading3: "", r1Date: new Date().toISOString(), update: true };
                    return updatedData;
                  });
                  return nextIndex;
                });
              }}
              style={{
                width: "150px",
                marginBottom: "20px",
                margin: "10px",
                float: "right",
              }}
              className="btn btn-primary text-white pr-2"
            >
              Next
            </button> */}

            </DialogActions>
          </form>
        </Dialog>

        <Dialog
            open={showHistory !== null}
            onClose={() => {
              setShowHistory(null);
            }}
            maxWidth="lg"
            fullWidth
        >
          <DialogTitle>Reading History {" "}
            {formData[showHistory]?.assetId
                ? "(" +
                siteAssets
                    .filter((a) => a.assetId == formData[showHistory]?.assetId)
                    .map(
                        (option) => option.assetId + " "+ option.assetName + " - " + option.category
                    )[0] +
                ")"
                : ""} {" - "} {getName(showHistory)}</DialogTitle>
          <DialogContent dividers>
            <Fragment>
              <Grid container>
                <Grid sm={12}>
                  <div className="table-responsive" style={{ marginTop: "30px" }}>
                    <table className="table table-bordered f-11">
                      <thead className="table-dark">
                      <tr>
                        <th>Test Date</th>
                        <th>Expiry Date</th>
                        <th>Reading 1</th>
                        <th>Reading 2</th>
                        <th>Reading 3</th>
                      </tr>
                      </thead>
                      <tbody>
                      {alldata
                          ?.filter(
                              (a) =>
                                  a.reading1 && a.reading2 && a.reading3 &&
                                  a.assetId === formData[showHistory]?.assetId &&
                                  a.temperature ===
                                  formData[showHistory]?.temperature &&
                                  a.normalRunTime ===
                                  formData[showHistory]?.normalRunTime &&
                                  a.outletType ===
                                  formData[showHistory]?.outletType &&
                                  a.usageFrequency ===
                                  formData[showHistory]?.usageFrequency &&
                                  a.floor === formData[showHistory]?.floor &&
                                  a.room === formData[showHistory]?.room
                          )
                          ?.map((i) => (
                              <tr>
                                <td>
                                  {i?.r1Date
                                      ? moment(i?.r1Date).format("DD/MM/YYYY")
                                      : ""}
                                </td>
                                <td>
                                  {i?.r1Date
                                      ? dateFormatFromFrequency(
                                          i?.r1Date?.split("T")?.[0]
                                      )
                                      : ""}
                                </td>
                                <td
                                    style={{
                                      color:
                                          (i?.temperature === "Hot" &&
                                              Number(i?.reading1) < 50) ||
                                          (i?.temperature === "Cold" &&
                                              Number(i?.reading1) > 20)
                                              ? "red"
                                              : "green",
                                      fontWeight: "600",
                                    }}
                                >
                                  {i?.reading1}
                                </td>
                                <td
                                    style={{
                                      color:
                                          (i?.temperature === "Hot" &&
                                              Number(i?.reading1) < 50) ||
                                          (i?.temperature === "Cold" &&
                                              Number(i?.reading1) > 20)
                                              ? "red"
                                              : "green",
                                      fontWeight: "600",
                                    }}
                                >
                                  {i?.reading2}
                                </td>
                                <td
                                    style={{
                                      color:
                                          (i?.temperature === "Hot" &&
                                              Number(i?.reading1) < 50) ||
                                          (i?.temperature === "Cold" &&
                                              Number(i?.reading1) > 20)
                                              ? "red"
                                              : "green",
                                      fontWeight: "600",
                                    }}
                                >
                                  {i?.reading3}
                                </td>
                              </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </Grid>
              </Grid>
            </Fragment>
          </DialogContent>
          <DialogActions>

            <Button
                className="bg-primary text-white"
                onClick={() => {
                  setShowHistory(null);
                }}
            >
              Close
            </Button>
            {showHistory < (formData.length - 1) &&
                <Button
                    className="bg-primary text-white"
                    onClick={() => {
                      setShowHistory((prev) => (prev < formData.length - 1 ? prev + 1 : prev));
                    }}
                >
                  Next
                </Button>
            }
          </DialogActions>
        </Dialog>

        <form onSubmit={addSiteCheckSurvey2}>
          <Grid container>
            <Grid sm={4}>
              <Typography variant="h6" gutterBottom>
                Water - Outlet Temperature - Tests{" "}
                <Chip
                    color={completed ? "success" : "warning"}
                    label={"In progress"}
                />
              </Typography>
            </Grid>
            <Grid sm={4}>
              <button
                  type="button"
                  disabled={isDownloadingPdf || isLoading || !checkId}
                  title="Download a fresh PDF of all latest saved water temperature readings"
                  style={{
                    minWidth: "150px",
                    marginBottom: "20px",
                    margin: "10px",
                    float: "right",
                  }}
                  className="btn btn-outline-primary"
                  onClick={downloadLatestPdf}
              >
                {isDownloadingPdf ? "Preparing PDF..." : "Download PDF"}
              </button>
            </Grid>
            <Grid sm={4}>
              <button
                  type="button"
                  style={{
                    width: "150px",
                    marginBottom: "20px",
                    margin: "10px",
                    float: "right",
                  }}
                  className="btn btn-primary btn-light"
                  onClick={(e) => {
                    e.preventDefault();
                    const uformData = [...formData];
                    uformData.push({new: true});
                    setFormData(uformData);
                  }}
              >
                Add outlet
              </button>
            </Grid>

            <Grid sm={12}>
              {isAdmin && formData.some((item) => item?.completed) && (
                  <div className="alert alert-info py-2 mb-2">
                    <i className="fas fa-grip-vertical me-2"></i>
                    Admin: drag saved outlets or type an Order number to move them, and edit the saved dropdown fields directly.
                    Click Save to persist the highlighted changes.
                  </div>
              )}
              {hasUnsavedAdminChanges && (
                  <div className="alert alert-warning py-2 mb-2">
                    <strong>Unsaved changes:</strong> changed cells are highlighted.
                    Save before leaving this Site Check, or you will be asked to confirm leaving without saving.
                  </div>
              )}
              <div className="table-responsive">
                <table className="table table-bordered f-11">
                  <thead className="table-dark">
                  <tr>
                    <th scope="col">Order</th>
                    <th scope="col">Asset</th>
                    <th scope="col">Outlet Type</th>
                    <th scope="col">Temperature</th>
                    <th scope="col">Norm Run Time</th>
                    <th scope="col">Usage Frequency</th>
                    <th scope="col">Floor</th>
                    <th scope="col">Room</th>
                    <th scope="col">Readings</th>
                    <th scope="col"></th>
                  </tr>
                  </thead>
                  <tbody>
                  {isLoading && (
                      <tr>
                        <td colSpan={9} align="center"><CircularProgress /></td>
                      </tr>
                  )}
                  {!isLoading &&
                      formData?.map((d, idx) => {
                        const isAllFilled =
                            formData[idx].assetId &&
                            formData?.[idx]?.outletType &&
                            formData?.[idx]?.temperature &&
                            formData?.[idx]?.normalRunTime &&
                            formData?.[idx]?.usageFrequency &&
                            formData?.[idx]?.floor &&
                            formData?.[idx]?.room;
                        const assetName = siteAssets
                            .filter((a) => a.assetId == formData[idx].assetId)
                            .map(
                                (option) => option.assetId + " - "+ option.assetName + " - " + option.category
                            )?.[0];
                        const isPersistedRow = Boolean(
                            formData?.[idx]?.id && formData?.[idx]?.assetId
                        );
                        if (!(isAdmin && isPersistedRow)) {
                          formData[idx].completed =
                              formData?.[idx]?.completed && isAllFilled;
                        }
                        const canAdminEditSavedRow = isAdmin && isPersistedRow;
                        const changedFields = getChangedFields(formData?.[idx]);
                        const changedFieldSet = new Set(changedFields);
                        const rowHasUnsavedChanges = changedFields.length > 0;
                        const changedCellClass = (field) =>
                            changedFieldSet.has(field) ? "table-warning" : "";
                        // Asset options: exclude only other rows' assets so current row's selection stays visible
                        // Assets must have category=Mechanical, subCategory=Water Services, subCategory2=Outlet, subCategory3=Tap or Shower (add via Site > Assets)
                        const assetOptions = siteAssets
                            .filter(s => s.category === "Mechanical" && s.subCategory === "Water Services" && s.subCategory2 === "Outlet" && (s.subCategory3 === "Tap" || s.subCategory3 === "Shower"))
                            .filter(s => !formData?.filter((_, i) => i !== idx).map(a => a.assetId).includes(s.assetId))
                            .map((option) => ({
                              key: option.assetId,
                              label: option.assetId + " - " + option.assetName + " - " + option.category,
                            }));
                        const selectedAssetOption = assetOptions.find(o => o.key == formData[idx]?.assetId) || null;
                        console.log("[SurveyWaterTemp] row", idx, "assetId", formData[idx]?.assetId, "assetOptions", assetOptions?.length, "selected", selectedAssetOption, "siteAssets", siteAssets?.length);
                        return (
                            <tr
                                key={formData?.[idx]?.assetId ? `asset-${formData[idx].assetId}` : `new-${idx}`}
                                onDragOver={(e) => {
                                  if (isAdmin && formData?.[idx]?.completed && draggedRowIndex !== null) {
                                    e.preventDefault();
                                  }
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  handleCompletedOutletDrop(draggedRowIndex, idx);
                                }}
                            >
                              <td className={changedCellClass("sortOrder")}>
                                {formData?.[idx]?.completed ? (
                                    <SortableOrderControl
                                        value={formData?.[idx]?.sortOrder}
                                        max={formData.filter((item) => item?.id && item?.assetId).length}
                                        canEdit={isAdmin && isPersistedRow}
                                        dirty={changedFieldSet.has("sortOrder")}
                                        onMove={(position) =>
                                            movePersistedOutletToPosition(formData?.[idx]?.id, position)
                                        }
                                        onDragStart={(e) => {
                                          setDraggedRowIndex(idx);
                                          e.dataTransfer.effectAllowed = "move";
                                          e.dataTransfer.setData(
                                              "text/plain",
                                              String(formData?.[idx]?.assetId ?? idx)
                                          );
                                        }}
                                        onDragEnd={() => setDraggedRowIndex(null)}
                                        title="Drag the handle or type an order number, then Save"
                                    />
                                ) : (
                                    <input
                                        value={formData?.[idx]?.sortOrder}
                                        type="number"
                                        min="1"
                                        name="sortOrder"
                                        id="sortOrder"
                                        className="form-control"
                                        onChange={(e) => handleInputChange(e, idx)}
                                        required
                                    /> )}
                              </td>
                              <td>
                                {formData?.[idx]?.completed ? (
                                    <p>{assetName}</p>
                                ) : (
                                    <Autocomplete
                                        id={`assetId-${idx}`}
                                        disabled={formData?.[idx]?.completed}
                                        value={selectedAssetOption}
                                        onChange={(event, item) => {
                                          const uformData = [...formData];
                                          uformData[idx].assetId = item?.key;
                                          setFormData(uformData);
                                        }}
                                        options={assetOptions}
                                        getOptionLabel={(option) => option?.label ?? ""}
                                        renderInput={(params) => (
                                            <div ref={params.InputProps.ref}>
                                              <input
                                                  type="text"
                                                  autoComplete="off"
                                                  readOnly
                                                  onFocus={(e) => e.target.removeAttribute("readonly")}
                                                  {...params.inputProps}
                                                  required
                                                  disabled={formData?.[idx]?.completed}
                                                  className="form-control"
                                              />
                                            </div>
                                        )}
                                    />
                                )}
                              </td>
                              <td className={changedCellClass("outletType")}>
                                <select
                                    disabled={formData?.[idx]?.completed && !canAdminEditSavedRow}
                                    name="outletType"
                                    className="form-control form-select"
                                    id="outletType"
                                    value={formData?.[idx]?.outletType}
                                    required
                                    onChange={(e) => handleInputChange(e, idx)}
                                >
                                  <option value="">Select Outlet Type</option>
                                  {outletoptions.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                  ))}
                                </select>
                              </td>
                              <td className={changedCellClass("temperature")}>
                                <select
                                    disabled={formData?.[idx]?.completed && !canAdminEditSavedRow}
                                    name="temperature"
                                    className="form-control form-select"
                                    id="temperature"
                                    required
                                    value={formData?.[idx]?.temperature}
                                    onChange={(e) => handleInputChange(e, idx)}
                                >
                                  <option value="">Select temperature</option>
                                  {tempratureoptions.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                  ))}
                                </select>
                              </td>

                              <td
                                  className={changedCellClass("normalRunTime")}
                                  style={{ width: "150px" }}
                              >
                                <select
                                    disabled={formData?.[idx]?.completed && !canAdminEditSavedRow}
                                    name="normalRunTime"
                                    className="form-control form-select"
                                    id="normalRunTime"
                                    required
                                    value={formData?.[idx]?.normalRunTime}
                                    onChange={(e) => handleInputChange(e, idx)}
                                >
                                  <option value="">Select</option>
                                  {normruntime.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                  ))}
                                </select>
                              </td>
                              <td className={changedCellClass("usageFrequency")}>
                                <select
                                    disabled={formData?.[idx]?.completed && !canAdminEditSavedRow}
                                    name="usageFrequency"
                                    className="form-control form-select"
                                    id="usageFrequency"
                                    required
                                    value={formData?.[idx]?.usageFrequency}
                                    onChange={(e) => handleInputChange(e, idx)}
                                >
                                  <option value="">Select frequency</option>
                                  <option value="None">None</option>
                                  <option value="Daily">Daily</option>
                                  <option value="Weekly">Weekly</option>
                                  <option value="Monthly">Monthly</option>
                                  <option value="Yearly">Yearly</option>
                                </select>
                              </td>
                              <td className={changedCellClass("floor")}>
                                {formData?.[idx]?.completed && !canAdminEditSavedRow ? (
                                    formData?.[idx]?.floor != 'null' ? getNodeName(formData?.[idx]?.floor) : '--'
                                ) : (
                                    <select
                                        disabled={formData?.[idx]?.completed && !canAdminEditSavedRow}
                                        className="form-control form-select"
                                        name="floor"
                                        value={getLayoutSelectValue(formData?.[idx]?.floor, "floor")}
                                        required
                                        onChange={(e) => handleInputChange(e, idx)}
                                    >
                                      <option value="">Select </option>
                                      {siteLayout
                                          .filter((site) => site.nodeType === "floor")
                                          .map((site) => (
                                              <option key={site.id} value={site.nodeName}>
                                                {site.nodeName}{" "}
                                              </option>
                                          ))}
                                    </select>)}
                              </td>
                              <td className={changedCellClass("room")}>
                                {formData?.[idx]?.completed && !canAdminEditSavedRow ? (
                                    formData?.[idx]?.room != 'null' ? getNodeName(formData?.[idx]?.room) : '--'
                                ) : (
                                    <select
                                        disabled={formData?.[idx]?.completed && !canAdminEditSavedRow}
                                        className="form-control form-select"
                                        name="room"
                                        value={getLayoutSelectValue(formData?.[idx]?.room, "room")}
                                        required
                                        onChange={(e) => handleInputChange(e, idx)}
                                    >
                                      <option value="">Select </option>
                                      {siteLayout
                                          //.filter((site) => site.nodeType === "room")
                                          .filter(
                                              (site) =>
                                                  site.nodeType === "room" &&
                                                  site.parentNode === getLayoutNodeByStoredValue(
                                                      formData?.[idx]?.floor,
                                                      "floor"
                                                  )?.id
                                          )
                                          .map((site) => (
                                              <option key={site.id} value={site.nodeName}>
                                                {site.nodeName}{" "}
                                              </option>
                                          ))}
                                    </select>
                                )}
                              </td>
                              <td style={{ width: "200px" }}>
                                <p>
                                  1st : {formData?.[idx]?.reading1 ?? ""}{" "}
                                  {formData?.[idx]?.r1Date
                                      ? "(" +
                                      moment(String(formData?.[idx]?.r1Date)).format("DD-MM-YYYY")+
                                      ")"
                                      : "N/A"}
                                </p>
                                <p>
                                  2nd : {formData?.[idx]?.reading2 ?? ""}{" "}
                                  {formData?.[idx]?.r1Date
                                      ? "(" +
                                      moment(formData?.[idx]?.r1Date).format("DD-MM-YYYY")+
                                      ")"
                                      : "N/A"}
                                </p>
                                <p>
                                  3rd : {formData?.[idx]?.reading3 ?? ""}{" "}
                                  {formData?.[idx]?.r1Date
                                      ? "(" +
                                      moment(formData?.[idx]?.r1Date).format("DD-MM-YYYY")+
                                      ")"
                                      : "N/A"}
                                </p>
                              </td>

                              <td style={{ width: "120px" }}>
                                {rowHasUnsavedChanges && (
                                    <div className="mb-1">
                                      <span className="badge bg-warning text-dark">
                                        Unsaved
                                      </span>
                                    </div>
                                )}
                                {isAllFilled && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light text-dark"
                                        onClick={() => {
                                          setReadingPopShow(true)
                                          setReadingPop(() => {
                                            const nextIndex = idx;
                                            // Ensure the next index has default/blank values
                                            setFormData((prevData) => {
                                              const updatedData = [...prevData];
                                              updatedData[nextIndex] = { ...updatedData[nextIndex], reading1: "", reading2: "", reading3: "", r1Date: new Date().toISOString(), update: true };
                                              return updatedData;
                                            });
                                            return nextIndex;
                                          });
                                        }}
                                    >
                                      <i className="fas fa-chart-line"></i>
                                    </button>
                                )}
                                &nbsp;
                                {isAllFilled && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light text-dark"
                                        onClick={() => {
                                          setShowHistory(idx);
                                        }}
                                    >
                                      <i className="fas fa-clock"></i>
                                    </button>
                                )}
                                &nbsp;
                                {!formData?.[idx]?.completed &&
                                    <button
                                        disabled={formData?.[idx]?.completed}
                                        className="btn btn-sm btn-light text-dark"
                                        onClick={() => {
                                          const uformData = [...formData];
                                          uformData.splice(idx, 1);
                                          setFormData(uformData);
                                        }}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                }
                              </td>
                            </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </Grid>
            <Grid sm={12}>
              <button
                  style={{
                    width: "150px",
                    marginBottom: "20px",
                    margin: "10px",
                    float: "right",
                  }}
                  className="btn btn-primary text-white pr-2"
                  type="submit"
              >
                Save
              </button>
            </Grid>
          </Grid>
        </form>
      </>
  );
};

const mapStateToProps = (state) => ({
  siteAssets: state.site.siteAssets,
  siteSelectedForGlobal: state.site.siteSelectedForGlobal,
  siteLayout: state.site.siteLayout,
  loggedInUserData: state.site.loggedInUserData,
});

export default connect(mapStateToProps, { getSiteAssets, getSiteLayout })(
    SurveyWaterTemperatureMonitoring
);
